import { BlocksSnippetAri } from '@atlassian/ari';

import { ADFEntity } from '@atlaskit/adf-utils/types';
import { cfetch } from '@confluence/network';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData';

import {
	type Snippet,
	SnippetContentFormat,
	type SnippetOwner,
	type SnippetProduct,
} from '../snippetTypes';
import { parseSnippetContent } from '../snippetUtils';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
};

const BLOCK_SERVICE_API_URL = '/gateway/api/blocks/v1';
const SNIPPETS_PATH = '/snippets';
const PRODUCT_CONFLUENCE = 'CONFLUENCE';

export class SnippetError extends Error {
	constructor(readonly status: number) {
		super(`Snippet error: ${status}`);
	}
}

export type SnippetData = {
	resourceId: string; // ARI of the Snippet
	snippetId: string;
	name: string; // User defined name
	description?: string; // User defined name
	content: string; // String of the type defined in `contentFormat` (example: "ADF")
	contentFormat: SnippetContentFormat;
	product: SnippetProduct;
	label?: string;
	isFavorite: boolean;
	createdBy: string;
	createdAt: number;
	updatedAt: number;
};

const transformSnippets = async (snippets: SnippetData[]): Promise<Snippet[]> => {
	const { displayName } = await getSessionData();
	return snippets
		.filter((snippet: SnippetData) => snippet.contentFormat === SnippetContentFormat.ADF)
		.map((snippet: SnippetData) => transformSnippetData(snippet, displayName));
};

const transformSnippetData = (snippet: SnippetData, displayName: string | null): Snippet => {
	const createdBy: SnippetOwner = {
		accountId: snippet.createdBy,
		displayName:
			snippet.createdBy?.toLowerCase() === 'atlassian'
				? 'Atlassian'
				: displayName || snippet.createdBy,
	};

	return {
		ari: BlocksSnippetAri.parse(snippet.resourceId),
		snippetId: snippet.snippetId,
		name: snippet.name,
		description: snippet.description,
		contentFormat: snippet.contentFormat,
		content: parseSnippetContent(snippet.content),
		product: snippet.product,
		isFavorite: snippet.isFavorite,
		createdBy,
		createdAt: new Date(snippet.createdAt),
		updatedAt: new Date(snippet.updatedAt),
	};
};

export type CreateSnippetBody = {
	name: string;
	body: ADFEntity;
	bodyFormat: SnippetContentFormat;
	product: SnippetProduct;
};

/**
 * Fetch snippets from the Block Service for Confluence. https://bitbucket.org/atlassian/block-service/src/main/
 * Please note that no error handling is done. Ensure you are handling bad response codes when using this.
 */
export const fetchSnippets = async (): Promise<Snippet[]> => {
	const params = new URLSearchParams({
		product: PRODUCT_CONFLUENCE,
	});

	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${params.toString()}`, {
		method: 'GET',
		headers: COMMON_HEADERS,
	});

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	const responseData = await response.json();
	if (!responseData.data) {
		return [];
	}

	const adfSnippets = (responseData.data as SnippetData[]).filter(
		(snippet: SnippetData) => snippet.contentFormat === SnippetContentFormat.ADF,
	);

	return await transformSnippets(adfSnippets);
};

export const createSnippet = async (params: CreateSnippetBody): Promise<SnippetData> => {
	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}`, {
		method: 'POST',
		headers: COMMON_HEADERS,
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	return response.json();
};
